import { useCallback, useEffect, useMemo, useRef } from "preact/hooks";
import { AltHintTarget } from "../../components/AltHintTarget/AltHintTarget.jsx";
import { ControlBar } from "../../components/ControlBar/ControlBar.jsx";
import { ProgressMeter } from "../../components/ProgressMeter/ProgressMeter.jsx";
import { Panel } from "../../components/Panel/Panel.jsx";
import { SearchField } from "../../components/SearchField/SearchField.jsx";
import { formatCurrency } from "../../lib/formatters.js";
import { createSectionNavigationBindings } from "../../lib/shortcuts/createSectionNavigationBindings.js";
import { onKey } from "../../lib/shortcuts/onKey.js";
import { handlePageEscapeReset, hasPageEscapeResetAction } from "../../lib/shortcuts/pageEscapeReset.js";
import { useEscapeStack } from "../../lib/shortcuts/useEscapeStack.js";
import { useShortcutBindings } from "../../lib/shortcuts/useShortcutBindings.js";
import { usePageStateMemory } from "../../lib/usePageStateMemory.js";
import { useSessionStorageState } from "../../lib/useSessionStorageState.js";
import {
  setAnalysisChartCategory,
  setAnalysisChartPaymentMethod,
  setAnalysisChartSupplier,
  setBudgetSearchQuery,
  setExpensesSearchQuery
} from "../../lib/pageSearchState.js";
import {
  buildAnalysisExplorerRows,
  buildAnalysisExplorerSearchRows,
  getAnalysisExplorerGroupKeyAtDepth,
  isAnalysisExplorerMatrixPreset,
  listAnalysisExplorerPresets
} from "./analysisExplorer.js";
import { HierarchicalReviewSurface } from "./HierarchicalReviewSurface.jsx";
import "./AnalysisExplorer.css";

const ANALYSIS_EXPLORER_SEARCH_SESSION_KEY = "wallet-watch:analysis-explorer-search";
const ANALYSIS_EXPLORER_EXPANDED_SESSION_KEY = "wallet-watch:analysis-explorer-expanded";
const ANALYSIS_EXPLORER_SELECTION_SESSION_KEY = "wallet-watch:analysis-explorer-selection";
const ANALYSIS_EXPLORER_SORT_SESSION_KEY = "wallet-watch:analysis-explorer-sort";

function deserializeExpandedByPreset(storedValue) {
  try {
    const parsed = JSON.parse(storedValue);
    if (!parsed || typeof parsed !== "object") {
      return {};
    }

    return Object.fromEntries(
      Object.entries(parsed).map(([presetKey, rowIds]) => [
        presetKey,
        new Set(Array.isArray(rowIds) ? rowIds.filter((value) => typeof value === "string") : [])
      ])
    );
  } catch {
    return {};
  }
}

function serializeExpandedByPreset(value) {
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(value ?? {}).map(([presetKey, rowIds]) => [
        presetKey,
        Array.from(rowIds ?? [])
      ])
    )
  );
}

function ExplorerRow({
  row,
  rowIndex,
  isSelected,
  labelRef,
  onSelect,
  onToggle,
  distributionDepth
}) {
  const hasChildren = Boolean(row.children?.length);
  const showDistribution = row.depth === distributionDepth;

  return (
    <tr
      data-row-index={rowIndex}
      class={[
        "analysis-explorer-row",
        hasChildren ? "analysis-explorer-row--branch" : "",
        hasChildren && row.expanded ? "analysis-explorer-row--expanded" : "",
        isSelected ? "analysis-explorer-row--selected" : ""
      ].filter(Boolean).join(" ")}
      onClick={() => {
        onSelect(row.id);
        if (hasChildren) {
          onToggle(row.id);
        }
      }}
    >
      <td class="analysis-explorer-cell analysis-explorer-cell-label">
        <div class="analysis-explorer-label-wrap" style={{ "--analysis-node-depth": row.depth }}>
          {hasChildren ? (
            <button
              class="analysis-explorer-toggle"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onToggle(row.id);
              }}
            >
              {row.expanded ? "-" : "+"}
            </button>
          ) : (
            <span class="analysis-explorer-toggle-placeholder" />
          )}
          <span class="analysis-explorer-label-text" ref={isSelected ? labelRef : null}>{row.label}</span>
        </div>
      </td>
      <td class="analysis-explorer-cell analysis-explorer-cell-amount">{formatCurrency(row.amount)}</td>
      <td class="analysis-explorer-cell analysis-explorer-cell-percent">
        {showDistribution ? `${Math.round(row.percent)}%` : ""}
      </td>
      <td class="analysis-explorer-cell analysis-explorer-cell-meter">
        {showDistribution ? (
          <ProgressMeter
            levels={[
              {
                key: "share",
                percent: row.percent,
                tone: "good"
              }
            ]}
          />
        ) : null}
      </td>
    </tr>
  );
}

function MatrixCell({ row, t = (key) => key }) {
  return (
    <td class="analysis-explorer-cell analysis-explorer-cell-matrix">
      <div class="analysis-explorer-matrix" aria-label={t("months")}>
        {row.monthTotals.map((amount, monthIndex) => {
          const intensity = row.maxMonthAmount > 0
            ? Math.max(0, Math.min(amount / row.maxMonthAmount, 1))
            : 0;
          const inkColor = intensity >= 0.55
            ? "var(--color-success-ink)"
            : "var(--color-ink)";

          return (
            <span
              key={`${row.id}-month-${monthIndex + 1}`}
              class={[
                "analysis-explorer-matrix-cell",
                amount === 0 ? "analysis-explorer-matrix-cell--zero" : ""
              ].filter(Boolean).join(" ")}
              style={{
                "--analysis-matrix-fill": `${Math.round(intensity * 100)}%`,
                "--analysis-matrix-ink": inkColor
              }}
              title={`${monthIndex + 1}: ${formatCurrency(amount)}`}
            >
              {monthIndex + 1}
            </span>
          );
        })}
      </div>
    </td>
  );
}

function ExplorerMatrixRow({
  row,
  rowIndex,
  isSelected,
  labelRef,
  onSelect,
  onToggle,
  t = (key) => key
}) {
  const hasChildren = Boolean(row.children?.length);

  return (
    <tr
      data-row-index={rowIndex}
      class={[
        "analysis-explorer-row",
        "analysis-explorer-row--matrix",
        hasChildren ? "analysis-explorer-row--branch" : "",
        hasChildren && row.expanded ? "analysis-explorer-row--expanded" : "",
        isSelected ? "analysis-explorer-row--selected" : ""
      ].filter(Boolean).join(" ")}
      onClick={() => {
        onSelect(row.id);
        if (hasChildren) {
          onToggle(row.id);
        }
      }}
    >
      <td class="analysis-explorer-cell analysis-explorer-cell-label">
        <div class="analysis-explorer-label-wrap" style={{ "--analysis-node-depth": row.depth }}>
          {hasChildren ? (
            <button
              class="analysis-explorer-toggle"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onToggle(row.id);
              }}
            >
              {row.expanded ? "-" : "+"}
            </button>
          ) : (
            <span class="analysis-explorer-toggle-placeholder" />
          )}
          <span class="analysis-explorer-label-text" ref={isSelected ? labelRef : null}>{row.label}</span>
        </div>
      </td>
      <MatrixCell row={row} t={t} />
    </tr>
  );
}

export function AnalysisExplorer({
  expenseRows = [],
  activePreset,
  onPresetChange,
  onNavigateToPage,
  showAltHints = false,
  t = (key) => key
}) {
  const searchInputRef = useRef(null);
  const presetSelectRef = useRef(null);
  const explorerRef = useRef(null);
  const presetSeparator = useMemo(() => (
    document?.documentElement?.dir === "rtl" ? "◂" : "▸"
  ), []);
  const presetOptions = useMemo(() => listAnalysisExplorerPresets(t, presetSeparator), [presetSeparator, t]);
  const [expandedByPreset, setExpandedByPreset] = usePageStateMemory(ANALYSIS_EXPLORER_EXPANDED_SESSION_KEY, {}, {
    deserialize: deserializeExpandedByPreset,
    serialize: serializeExpandedByPreset
  });
  const [selectedRowByPreset, setSelectedRowByPreset] = usePageStateMemory(ANALYSIS_EXPLORER_SELECTION_SESSION_KEY, {}, {
    deserialize(storedValue) {
      try {
        const parsed = JSON.parse(storedValue);
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch {
        return {};
      }
    },
    serialize(value) {
      return JSON.stringify(value ?? {});
    }
  });
  const [sortStateByPreset, setSortStateByPreset] = usePageStateMemory(ANALYSIS_EXPLORER_SORT_SESSION_KEY, {}, {
    deserialize(storedValue) {
      try {
        const parsed = JSON.parse(storedValue);
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch {
        return {};
      }
    },
    serialize(value) {
      return JSON.stringify(value ?? {});
    }
  });
  const [searchQuery, setSearchQuery] = useSessionStorageState(ANALYSIS_EXPLORER_SEARCH_SESSION_KEY, "");
  const presetValue = presetOptions.some((option) => option.value === activePreset)
    ? activePreset
    : presetOptions[0].value;
  const selectedRowId = selectedRowByPreset[presetValue] ?? null;
  const sortState = sortStateByPreset[presetValue] ?? null;
  const isMatrixPreset = isAnalysisExplorerMatrixPreset(presetValue);

  const setSelectedRowId = useCallback((nextRowId) => {
    setSelectedRowByPreset((current) => ({
      ...current,
      [presetValue]: nextRowId
    }));
  }, [presetValue, setSelectedRowByPreset]);
  const setSortState = useCallback((nextSortState) => {
    setSortStateByPreset((current) => ({
      ...current,
      [presetValue]: nextSortState
    }));
  }, [presetValue, setSortStateByPreset]);

  useEffect(() => {
    if (!onPresetChange) {
      return;
    }

    if (!presetOptions.some((option) => option.value === activePreset)) {
      onPresetChange(presetOptions[0].value);
    }
  }, [activePreset, onPresetChange, presetOptions]);

  const visibleRows = useMemo(() => {
    const trimmedQuery = searchQuery.trim().toLocaleLowerCase();

    if (trimmedQuery) {
      return buildAnalysisExplorerSearchRows(expenseRows, presetValue, trimmedQuery, t, sortState);
    }

    return buildAnalysisExplorerRows(
      expenseRows,
      presetValue,
      expandedByPreset[presetValue] ?? new Set(),
      t,
      sortState
    );
  }, [expenseRows, expandedByPreset, presetValue, searchQuery, sortState, t]);
  const distributionDepth = useMemo(() => (
    visibleRows.reduce((maxDepth, row) => Math.max(maxDepth, row.depth), 0)
  ), [visibleRows]);
  const headerCells = useMemo(() => (
    isMatrixPreset
      ? [
          {
            key: "group",
            label: t("group"),
            align: "text",
            sortable: true
          },
          {
            key: "months",
            label: t("months"),
            align: "text",
            sortable: false
          }
        ]
      : [
          {
            key: "group",
            label: t("group"),
            align: "text",
            sortable: true
          },
          {
            key: "spent",
            label: t("spent"),
            align: "number",
            sortable: true
          },
          {
            key: "share",
            label: `% ${t("share")}`,
            align: "number",
            sortable: true
          },
          {
            key: "meter",
            label: t("meter"),
            align: "text",
            sortable: true
          }
        ]
  ), [isMatrixPreset, t]);
  const toolbarActionsForRow = useCallback((row) => {
    const groupKey = getAnalysisExplorerGroupKeyAtDepth(presetValue, row?.depth);
    if (!row || !groupKey) {
      return [];
    }

    if (["month", "type"].includes(groupKey)) {
      return [{
        key: "show-in-expenses",
        label: t("expenses"),
        onSelect: () => {
          setExpensesSearchQuery(row.label);
          onNavigateToPage?.("expenses");
        }
      }];
    }

    if (groupKey === "method") {
      return [
        {
          key: "show-in-expenses",
          label: t("expenses"),
          onSelect: () => {
            setExpensesSearchQuery(row.label);
            onNavigateToPage?.("expenses");
          }
        },
        {
          key: "show-in-chart",
          label: t("chart"),
          onSelect: () => {
            setAnalysisChartPaymentMethod(row.label);
            onNavigateToPage?.("analysis", "charts");
          }
        }
      ];
    }

    if (groupKey === "supplier") {
      return [
        {
          key: "show-in-expenses",
          label: t("expenses"),
          onSelect: () => {
            setExpensesSearchQuery(row.label);
            onNavigateToPage?.("expenses");
          }
        },
        {
          key: "show-in-chart",
          label: t("chart"),
          onSelect: () => {
            setAnalysisChartSupplier(row.label);
            onNavigateToPage?.("analysis", "charts");
          }
        }
      ];
    }

    if (groupKey === "category") {
      return [
        {
          key: "show-in-expenses",
          label: t("expenses"),
          onSelect: () => {
            setExpensesSearchQuery(row.label);
            onNavigateToPage?.("expenses");
          }
        },
        {
          key: "show-in-budget",
          label: t("budget"),
          onSelect: () => {
            setBudgetSearchQuery(row.label);
            onNavigateToPage?.("budget");
          }
        },
        {
          key: "show-in-chart",
          label: t("chart"),
          onSelect: () => {
            setAnalysisChartCategory(row.label);
            onNavigateToPage?.("analysis", "charts");
          }
        }
      ];
    }

    return [];
  }, [onNavigateToPage, presetValue, t]);

  useEffect(() => {
    focusExplorer();
  }, []);

  function focusExplorer() {
    explorerRef.current?.focus({ preventScroll: true });
  }

  function handlePresetChange(nextPreset) {
    if (!nextPreset || nextPreset === presetValue) {
      return;
    }

    onPresetChange?.(nextPreset);
  }

  function toggleRow(rowId) {
    setExpandedByPreset((current) => {
      const nextExpanded = new Set(current[presetValue] ?? []);

      if (nextExpanded.has(rowId)) {
        nextExpanded.delete(rowId);
      } else {
        nextExpanded.add(rowId);
      }

      return {
        ...current,
        [presetValue]: nextExpanded
      };
    });
  }

  const pageBindings = [
    ...createSectionNavigationBindings({
      options: presetOptions,
      value: presetValue,
      onChange: handlePresetChange,
      allowInInput: true
    }),
    onKey("Alt+Q", () => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select?.();
    }, { allowInInput: true }),
    onKey("Alt+E", () => {
      presetSelectRef.current?.focus();
    }, { allowInInput: true }),
    onKey("Alt+W", () => {
      focusExplorer();
    }, { allowInInput: true })
  ];
  const hasExpandedRows = (expandedByPreset[presetValue] ?? new Set()).size > 0;
  const hasEscapeAction = hasPageEscapeResetAction({
    searchQuery,
    sortState,
    extraResetAvailable: hasExpandedRows
  });
  useEscapeStack(() => handlePageEscapeReset({
    searchQuery,
    setSearchQuery,
    sortState,
    setSortState,
    extraResetAvailable: hasExpandedRows,
    onExtraReset: () => {
      setExpandedByPreset((current) => ({
        ...current,
        [presetValue]: new Set()
      }));
    },
    onResetApplied: focusExplorer
  }), hasEscapeAction, -100);

  useShortcutBindings(window, pageBindings);

  return (
    <Panel
      className="analysis-explorer-panel fill-area"
      toolbar={(
        <ControlBar
          boxed={false}
          start={(
            <AltHintTarget label="Q" visible={showAltHints}>
              <SearchField
                className="analysis-explorer-search-input"
                inputRef={searchInputRef}
                onFocusMainArea={focusExplorer}
                onValueChange={setSearchQuery}
                placeholder={t("filter")}
                value={searchQuery}
              />
            </AltHintTarget>
          )}
          end={(
            <AltHintTarget label="E" visible={showAltHints}>
              <label class="analysis-explorer-view-control">
                <span class="analysis-explorer-view-label">{t("view")}</span>
                <select
                  class="control-bar-select analysis-explorer-view-select"
                  ref={presetSelectRef}
                  value={presetValue}
                  onInput={(event) => handlePresetChange(event.currentTarget.value)}
                >
                  {presetOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </AltHintTarget>
          )}
        />
      )}
    >
      <HierarchicalReviewSurface
        rows={visibleRows}
        selectedRowId={selectedRowId}
        onSelectedRowIdChange={setSelectedRowId}
        onToggleRow={toggleRow}
        headerCells={headerCells}
        sortState={sortState}
        onSortChange={isMatrixPreset ? null : setSortState}
        renderRow={({ row, rowIndex, isSelected, labelRef, onSelect, onToggle }) => (
          isMatrixPreset ? (
            <ExplorerMatrixRow
              key={row.id}
              rowIndex={rowIndex}
              row={row}
              isSelected={isSelected}
              labelRef={labelRef}
              onSelect={onSelect}
              onToggle={onToggle}
              t={t}
            />
          ) : (
            <ExplorerRow
              key={row.id}
              rowIndex={rowIndex}
              row={row}
              isSelected={isSelected}
              labelRef={labelRef}
              onSelect={onSelect}
              onToggle={onToggle}
              distributionDepth={distributionDepth}
            />
          )
        )}
        emptyContent={t("noExpenseDataYetForExplorer")}
        baseClassName="analysis-explorer"
        dataRole="analysis-explorer-grid"
        surfaceRef={explorerRef}
        showAltHints={showAltHints}
        toolbarPlacement="inline-end"
        toolbarOffset={24}
        getToolbarActions={toolbarActionsForRow}
      />
    </Panel>
  );
}
